import { useCallback, useEffect, useRef, useState } from "react";
import { RotateCcw, Check, Eye } from "lucide-react";
import { sfx } from "../../lib/audio";

const SIZE = 300;
const FONT = (s) => `${Math.round(s * 0.78)}px 'Noto Sans TC', 'Noto Serif TC', sans-serif`;

// Approximate tracing: validates how much of the glyph shape the child's strokes covered.
export const TraceCanvas = ({ glyph, onComplete, testid = "trace-canvas" }) => {
  const guide = useRef(null);
  const draw = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [result, setResult] = useState(null);

  const paintGuide = useCallback((progress = 1) => {
    const c = guide.current.getContext("2d");
    c.clearRect(0, 0, SIZE, SIZE);
    c.strokeStyle = "#E7D7C1";
    c.setLineDash([6, 6]);
    c.lineWidth = 1.5;
    c.strokeRect(4, 4, SIZE - 8, SIZE - 8);
    c.beginPath();
    c.moveTo(SIZE / 2, 4); c.lineTo(SIZE / 2, SIZE - 4); c.moveTo(4, SIZE / 2); c.lineTo(SIZE - 4, SIZE / 2);
    c.stroke();
    c.setLineDash([]);
    c.font = FONT(SIZE);
    c.textAlign = "center";
    c.textBaseline = "middle";
    c.fillStyle = "#F1E4CC";
    c.fillText(glyph, SIZE / 2, SIZE / 2 + 6);
    if (progress < 1) {
      c.save();
      c.beginPath();
      c.rect(0, 0, SIZE, SIZE * progress);
      c.clip();
      c.fillStyle = "#F59E0B";
      c.fillText(glyph, SIZE / 2, SIZE / 2 + 6);
      c.restore();
    }
    c.setLineDash([4, 5]);
    c.strokeStyle = "#C83E2B";
    c.lineWidth = 1.5;
    c.strokeText(glyph, SIZE / 2, SIZE / 2 + 6);
  }, [glyph]);

  const clear = () => {
    draw.current.getContext("2d").clearRect(0, 0, SIZE, SIZE);
    setResult(null);
  };

  useEffect(() => { paintGuide(); clear(); }, [paintGuide]); // eslint-disable-line react-hooks/exhaustive-deps

  const demo = () => {
    let p = 0;
    const tick = () => {
      p += 0.025;
      paintGuide(Math.min(p, 0.999));
      if (p < 1) requestAnimationFrame(tick);
      else setTimeout(() => paintGuide(), 400);
    };
    tick();
  };

  const pos = (e) => {
    const r = draw.current.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * SIZE, ((e.clientY - r.top) / r.height) * SIZE];
  };
  const down = (e) => { drawing.current = true; last.current = pos(e); draw.current.setPointerCapture(e.pointerId); };
  const move = (e) => {
    if (!drawing.current) return;
    const p = pos(e);
    const c = draw.current.getContext("2d");
    c.strokeStyle = "rgba(200,62,43,0.85)";
    c.lineWidth = 22;
    c.lineCap = "round";
    c.lineJoin = "round";
    c.beginPath();
    c.moveTo(...last.current);
    c.lineTo(...p);
    c.stroke();
    last.current = p;
  };
  const up = () => { drawing.current = false; };

  const check = () => {
    const m = document.createElement("canvas");
    m.width = m.height = SIZE;
    const mc = m.getContext("2d");
    mc.font = FONT(SIZE);
    mc.textAlign = "center";
    mc.textBaseline = "middle";
    mc.fillText(glyph, SIZE / 2, SIZE / 2 + 6);
    const mask = mc.getImageData(0, 0, SIZE, SIZE).data;
    const ink = draw.current.getContext("2d").getImageData(0, 0, SIZE, SIZE).data;
    let total = 0, hit = 0, inkTotal = 0, inkIn = 0;
    for (let i = 3; i < mask.length; i += 12) {
      const inGlyph = mask[i] > 100;
      const inked = ink[i] > 0;
      if (inGlyph) { total++; if (inked) hit++; }
      if (inked) { inkTotal++; if (inGlyph) inkIn++; }
    }
    const coverage = total ? hit / total : 0;
    const precision = inkTotal ? inkIn / inkTotal : 0;
    const ok = coverage >= 0.5 && precision >= 0.25;
    setResult(ok ? "good" : "retry");
    sfx(ok ? "good" : "bad");
    if (ok) onComplete && onComplete();
  };

  return (
    <div className="flex flex-col items-center gap-4" data-testid={testid}>
      <div className={`relative rounded-3xl bg-white border-4 ${result === "good" ? "border-[var(--jade)]" : "border-[var(--line)]"} shadow-inner`} style={{ width: SIZE, height: SIZE }}>
        <canvas ref={guide} width={SIZE} height={SIZE} className="absolute inset-0" />
        <canvas ref={draw} width={SIZE} height={SIZE} className="absolute inset-0 touch-none cursor-crosshair" data-testid={`${testid}-pad`}
          onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} />
        {result === "good" && <div className="absolute -top-4 -right-4 text-5xl" data-testid={`${testid}-success`}>🌟</div>}
      </div>
      {result === "retry" && <div className="font-serif-tc text-xl text-amber-700" data-testid={`${testid}-retry-msg`}>再描一次，沿著紅線走喔！</div>}
      <div className="flex gap-3">
        <button data-testid={`${testid}-demo-btn`} onClick={demo} className="kid-btn px-5 whitespace-nowrap bg-amber-100 text-[var(--tea)] flex items-center gap-2"><Eye /> 看一看</button>
        <button data-testid={`${testid}-clear-btn`} onClick={clear} className="kid-btn px-5 whitespace-nowrap bg-white border-2 border-[var(--line)] flex items-center gap-2"><RotateCcw /> 重來</button>
        <button data-testid={`${testid}-check-btn`} onClick={check} className="kid-btn px-6 whitespace-nowrap bg-[var(--jade)] text-white flex items-center gap-2"><Check /> 完成</button>
      </div>
    </div>
  );
};
